/**
 * Structured Error Reporting Service
 *
 * Provides a centralized way to handle errors with appropriate reporting levels:
 * - Critical errors -> Sentry + console.error
 * - Expected errors -> console.error only (user-facing validation, network offline, etc.)
 * - Debug info -> console.warn in development only
 *
 * Usage:
 *   import { errorReporter } from '@/services/errorReportingService';
 *
 *   // Critical error - goes to Sentry
 *   errorReporter.critical('Failed to fetch subscription data', error, {
 *     feature: 'subscription',
 *     operation: 'fetchPricingData',
 *     userId: user?.id
 *   });
 *
 *   // Expected error - console only
 *   errorReporter.expected('User cancelled upload', error);
 *
 *   // Debug info - dev only
 *   errorReporter.debug('Retrying API call', { attempt: 3 });
 */

import * as Sentry from '@sentry/react';

/**
 * Error severity levels for categorization
 */
export type ErrorSeverity = 'critical' | 'expected' | 'debug';

/**
 * Feature tags for error categorization in Sentry
 */
export type FeatureTag =
  /** App shell concerns: version checking, update prompts, bootstrap. */
  | 'app'
  | 'auth'
  | 'subscription'
  | 'notes'
  | 'video'
  | 'audio'
  | 'upload'
  | 'firebase'
  | 'stripe'
  | 'extension'
  | 'secondBrain'
  | 'onboarding'
  | 'settings'
  | 'projects'
  | 'support'
  | 'vibeCode'
  | 'recording'
  | 'noteFormats'
  | 'referral'
  | 'smartTags'
  | 'zapier';

/**
 * Context for error reporting
 */
interface ErrorContext {
  feature?: FeatureTag;
  operation?: string;
  userId?: string;
  noteId?: string;
  [key: string]: unknown;
}

/**
 * Check if Sentry client is initialized
 */
const isSentryInitialized = (): boolean => {
  return !!Sentry.getClient();
};

/**
 * Format error message with context
 */
const formatMessage = (prefix: string, message: string, context?: ErrorContext): string => {
  const contextStr = context?.feature ? `[${context.feature}]` : '';
  const operationStr = context?.operation ? ` ${context.operation}:` : '';
  return `${prefix}${contextStr}${operationStr} ${message}`;
};

/**
 * Structured error reporter
 */
export const errorReporter = {
  /**
   * Report a critical error that should go to Sentry
   * Use for: API failures, auth errors, data corruption, unexpected exceptions
   */
  critical: (message: string, error: unknown, context?: ErrorContext): void => {
    const formattedMessage = formatMessage('[CRITICAL]', message, context);

    // Always log to console for debugging
    console.error(formattedMessage, error);

    // Report to Sentry with context
    // Wrap plain objects in a real Error so Sentry doesn't report
    // "Object captured as exception with keys: ..."
    if (isSentryInitialized()) {
      const sentryError =
        error instanceof Error
          ? error
          : new Error(
              typeof error === 'object' && error !== null && 'message' in error
                ? String((error as { message: unknown }).message)
                : message,
            );
      Sentry.captureException(sentryError, {
        tags: {
          feature: context?.feature,
          severity: 'critical',
        },
        extra: {
          message,
          operation: context?.operation,
          ...context,
        },
      });
    }
  },

  /**
   * Report an expected error that should NOT go to Sentry
   * Use for: User cancelled actions, validation failures, network offline, expected 4xx errors
   */
  expected: (message: string, error?: unknown, context?: ErrorContext): void => {
    const formattedMessage = formatMessage('[EXPECTED]', message, context);
    console.error(formattedMessage, error);
    // Intentionally NOT sending to Sentry - these are expected conditions
  },

  /**
   * Log debug information (development only)
   * Use for: Retry attempts, fallback paths, non-critical warnings
   */
  debug: (message: string, context?: ErrorContext): void => {
    if (import.meta.env.DEV) {
      const formattedMessage = formatMessage('[DEBUG]', message, context);
      console.warn(formattedMessage, context);
    }
  },

  /**
   * Report with explicit severity level
   * Useful when severity is determined dynamically
   */
  report: (severity: ErrorSeverity, message: string, error?: unknown, context?: ErrorContext): void => {
    switch (severity) {
      case 'critical':
        errorReporter.critical(message, error, context);
        break;
      case 'expected':
        errorReporter.expected(message, error, context);
        break;
      case 'debug':
        errorReporter.debug(message, context);
        break;
    }
  },

  /**
   * Capture a message to Sentry without an error object
   * Use for: Unexpected states, invariant violations, monitoring
   */
  captureMessage: (message: string, level: Sentry.SeverityLevel, context?: ErrorContext): void => {
    if (import.meta.env.DEV) {
      console.warn(`[Sentry Message] ${message}`, context);
    }

    if (isSentryInitialized()) {
      Sentry.captureMessage(message, {
        level,
        tags: {
          feature: context?.feature,
        },
        extra: context,
      });
    }
  },
};

/**
 * Helper to determine if an error is an expected API error
 * Returns true for 4xx errors that are user-facing (validation, not found, etc.)
 */
export const isExpectedApiError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as { status?: number; response?: { status?: number } };
  const status = err.status || err.response?.status;

  // 4xx errors except 401/403 (auth issues should be tracked)
  if (status && status >= 400 && status < 500) {
    // Auth errors should still be tracked
    if (status === 401 || status === 403) return false;
    return true;
  }

  return false;
};

/**
 * Helper to determine if an error is a network/connectivity issue
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as { message?: string; name?: string };
  const message = err.message?.toLowerCase() || '';
  const name = err.name?.toLowerCase() || '';

  return (
    name === 'networkerror' ||
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('offline')
  );
};

/**
 * Helper to determine if an error is a Firebase offline/connectivity error.
 * These occur when Firestore operations fail because the client has no network connection.
 * They are environmental (not bugs) and should be treated as expected errors.
 */
export const isFirebaseOfflineError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as { message?: string; code?: string; name?: string };
  const message = err.message?.toLowerCase() || '';
  const name = err.name?.toLowerCase() || '';

  return (
    (name === 'firebaseerror' && message.includes('client is offline')) ||
    (name === 'firebaseerror' && message.includes('failed to get document') && message.includes('offline'))
  );
};

export default errorReporter;
